import type { PieceAcheminement, ReglementPiece } from '@admin-pmvb/core'
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { api, type DebitsPiece, ErreurApi, type FichePiece, lire, requetes } from '../api'
import { LIBELLES_ETAT_ACHEMINEMENT } from '../libelles'
import {
  CONSEQUENCES_REGLEMENT,
  ligneDebit,
  reglementRecommande,
  texteAucunDebit,
} from './reglement-trouve'

// Acheminement d'une pièce rattachée, vers le Drive (archive) et le cabinet (adresse de
// collecte) : son état et les décisions qu'un humain doit prendre (réglée par la société ou
// avancée, envoi interrompu, dépôt à la main, nouvel essai). Le suivi de chaque destination
// est dans l'assistant de la fiche (AssistantPiece). Pour « réglée par la société ou avancée »,
// les débits trouvés sur les relevés d'abord, puis chaque réponse avec ce qu'elle fait.

export type Acheminable = Pick<
  FichePiece,
  | 'id'
  | 'etat'
  | 'dossierId'
  | 'rangement'
  | 'cheminDrive'
  | 'lienDrive'
  | 'rangementErreur'
  | 'rangementTentatives'
  | 'transmission'
  | 'transmiseA'
  | 'transmiseLe'
  | 'transmissionErreur'
  | 'transmissionTentatives'
  | 'reglement'
  | 'payeurAttendu'
  | 'boite'
  | 'dejaChezCabinet'
>

const TON: Partial<Record<FichePiece['etat'], string>> = {
  a_decider: 'ton-ambre',
  en_attente: 'ton-ambre',
  drive_echec: 'ton-alerte',
  tiime_echec: 'ton-alerte',
  termine: 'ton-ok',
}

export function BadgeAcheminement({ etat }: { etat: FichePiece['etat'] }) {
  return <span className={`badge ${TON[etat] ?? ''}`}>{LIBELLES_ETAT_ACHEMINEMENT[etat]}</span>
}

/** Les boutons de décision, selon l'état de la pièce (fiche de pièce, fiche de tâche). */
export function DecisionsAcheminement({ p }: { p: Acheminable }) {
  const client = useQueryClient()
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)
  const param = { param: { id: p.id } }

  async function faire(requete: () => Promise<unknown>) {
    setErreur(null)
    setEnCours(true)
    try {
      await requete()
      await client.invalidateQueries()
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Action impossible.')
    } finally {
      setEnCours(false)
    }
  }
  const regler = (reglement: ReglementPiece) =>
    faire(() => lire(api.pieces[':id'].reglement.$post({ ...param, json: { reglement } })))
  const agir = (action: PieceAcheminement['action']) =>
    faire(() => lire(api.pieces[':id'].acheminement.$post({ ...param, json: { action } })))

  // Tant que rien n'est parti au cabinet, le règlement d'une facture de boîte perso se change.
  // Facture d'un portail d'achats : qui l'a payée se dit dans sa décision « qui a payé ».
  const aDecider = !p.payeurAttendu && p.transmission === 'a_decider'
  const reglable =
    aDecider ||
    (!p.payeurAttendu &&
      p.reglement !== null &&
      p.transmission !== 'faite' &&
      p.transmission !== 'incertain')
  // Question ouverte : ce que l'appli a trouvé sur les relevés, et la réponse qu'il désigne.
  const trouves = useQuery({ ...requetes.debitsPiece(p.id), enabled: aDecider })
  const recommande = reglementRecommande(trouves.data)
  const reglements: { reglement: ReglementPiece; libelle: string }[] = []
  if (reglable) {
    if (p.reglement !== 'societe')
      reglements.push({ reglement: 'societe', libelle: 'Réglée par la société' })
    if (p.reglement !== 'avance')
      reglements.push({ reglement: 'avance', libelle: 'Avancée personnellement' })
  }

  const boutons: { libelle: string; faire: () => void; principal?: boolean }[] = []
  if (p.transmission === 'incertain') {
    boutons.push({
      libelle: 'Arrivée dans Tiime',
      faire: () => void agir('arrivee'),
      principal: true,
    })
    boutons.push({ libelle: 'La renvoyer', faire: () => void agir('renvoyer') })
  }
  if (p.transmission === 'manuelle')
    boutons.push({
      libelle: 'Déposée à la main',
      faire: () => void agir('deposee_a_la_main'),
      principal: true,
    })
  if (
    p.etat === 'drive_echec' ||
    p.etat === 'tiime_echec' ||
    p.rangement === 'en_attente' ||
    (p.rangement === 'a_faire' && p.rangementTentatives > 0) ||
    (p.transmission === 'a_faire' && p.transmissionTentatives > 0)
  )
    boutons.push({
      // Sans dossier, réessayer redemande le placement à l'assistant : on le dit tel quel.
      libelle:
        p.dossierId === null && p.rangement === 'en_attente'
          ? 'Redemander à l’assistant de classer'
          : 'Réessayer maintenant',
      faire: () => void agir('reessayer'),
    })

  if (reglements.length === 0 && boutons.length === 0) return null
  return (
    <>
      {aDecider && <DebitsTrouves etat={trouves} />}
      {reglements.length > 0 && (
        <div className="choix-reglement">
          {reglements.map((r) => (
            <div key={r.reglement} className="choix-reglement-option">
              <button
                type="button"
                className={aDecider && recommande === r.reglement ? 'bouton' : 'bouton-discret'}
                disabled={enCours}
                onClick={() => void regler(r.reglement)}
              >
                {r.libelle}
              </button>
              <p className="discret">{CONSEQUENCES_REGLEMENT[r.reglement]}</p>
            </div>
          ))}
        </div>
      )}
      {boutons.length > 0 && (
        <div className="boutons">
          {boutons.map((b) => (
            <button
              key={b.libelle}
              type="button"
              className={b.principal ? 'bouton' : 'bouton-discret'}
              disabled={enCours}
              onClick={b.faire}
            >
              {b.libelle}
            </button>
          ))}
        </div>
      )}
      {erreur && <p className="alerte">{erreur}</p>}
    </>
  )
}

/** Les débits trouvés pour une facture dont le règlement reste à dire (tâche d'un mail). */
export function DebitsDeLaPiece({ pieceId }: { pieceId: string }) {
  const etat = useQuery(requetes.debitsPiece(pieceId))
  return <DebitsTrouves etat={etat} />
}

/**
 * Ce que l'appli a trouvé sur les relevés pour la facture (comptes que l'on voit) : les débits
 * qui concordent, chacun menant à son opération, et pourquoi elle n'a pas tranché seule ; ou
 * où elle a cherché sans rien trouver.
 */
function DebitsTrouves({ etat }: { etat: UseQueryResult<DebitsPiece> }) {
  if (etat.isPending) return <p className="discret">Recherche du débit sur les relevés…</p>
  const lu = etat.data
  if (!lu?.applicable) return null
  if (lu.debits.length === 0) return <p className="discret">{texteAucunDebit(lu.entites)}</p>
  return (
    <div className="debits-trouves">
      <p>{lu.debits.length === 1 ? 'Débit trouvé :' : 'Débits trouvés :'}</p>
      <ul className="liste-simple">
        {lu.debits.map((d) => (
          <li key={d.transactionId}>
            <Link to="/rapprochements/$id" params={{ id: d.transactionId }}>
              {ligneDebit(d)}
            </Link>
          </li>
        ))}
      </ul>
      {lu.motif && <p className="discret">{lu.motif}</p>}
    </div>
  )
}
